<template>
  <div :class="['zm-pin-code', 'zm-pin-code-' + size]">
    <div class="zm-pin-last-input-hover"></div>
    <input
      v-bind="$attrs"
      v-model="code"
      ref="input"
      class="zm-pin-code__input"
      type="text"
      @keyup.enter="onEnter"
      @paste.prevent="onPasted"
      @keydown="onKeydown"
      @focus="onInputFocus"
      @click="onClick"
      @input="onInput"
      maxlength="6"
      autofocus
      :style="{'caret-color': code.length >= 6 ? 'transparent' : 'auto'}"
      spellcheck="false"
    />
    <div class="zm-pin-code__group">
      <div
        class="zm-pin-code--item"
        :class="{'is-focus': code.length === (n-1) || (cursorIndex === n && n ===6)}"
        v-for="n in 6" :key="n"
      />
    </div>
  </div>
</template>

<script>
  import AriaUtils from '../../utils/aria-utils';

  export default {
    name: 'ZmPinCode',
    data() {
      return {
        code: this.modelValue,
        cursorIndex: 0
      };
    },
    props: {
      modelValue: {
        type: String,
        default: null
      },
      isNumber: {
        type: Boolean,
        default: false
      },
      size: {
        type: String,
        default: 'middle'
      }
    },
    model: {
      prop: 'modelValue',
      event: 'change'
    },
    watch: {
      code(newVal) {
        const selectionStart = this.$refs.input.selectionStart;
        const selectionEnd = this.$refs.input.selectionEnd;
        if (selectionStart === selectionEnd) {
          this.cursorIndex = selectionEnd;
        }
        this.$emit('change', newVal);
      },
      modelValue(newVal) {
        if (newVal === '') {
          this.cursorIndex = 0;
        }
        if (!this.isNumber) {
          this.code = newVal;
        } else {
          this.code = String(newVal).replace(/\D/g, '');
        }
      }
    },
    mounted() {
      if (this.code.length) {
        this.cursorIndex = this.code.length;
      }
    },
    methods: {
      onKeydown(event) {
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;
        if ([keys.down, keys.up, keys.left, keys.right, keys.home, keys.end].indexOf(keyCode) !== -1) {
          event.preventDefault();
        }
      },
      focus() {
        this.$nextTick(() => {
          this.$refs.input && this.$refs.input.focus();
        });
      },
      onInput(e) {
        this.$emit('input', e);
      },
      onClick(e) {
        this.onInputFocus();
        e.preventDefault();
      },
      onInputFocus() {
        this.$refs.input.value = this.code;
        this.$refs.input.setSelectionRange(-1, -1);
      },
      onEnter(e) {
        this.$emit('enter', e);
      },
      onPasted(event) {
        const pastedValue = event.clipboardData.getData('text/plain');
        let canPaste = true;
        if (this.isNumber) {
          const regx = new RegExp('^\\d{6}$');
          canPaste = regx.test(pastedValue);
        }
        if (canPaste) {
          this.code = pastedValue;
        }
      }
    }
  };
</script>
<!--
CSS: 关于字体大小和间距以及缩进的计算
1. 首先确认单个数字框的宽度（W），用作间距计算的基准，然后确认数字框之间的间距（M），比如该组件默认提供两个尺寸都是以44px 宽度，间距是10px，则W=44，M=10
2. 确认输入框字体大小对应的单个字符宽度（F），有两点需要特殊注意，
一是字体必须是等宽字体，
二是设置的字体大小应用后所渲染的字符宽度越接近整数越好（字体大小是28px时，单个字符宽度是16.86，而字体大小是30px，单个字符串宽度是18.06，）
3. 缩进的值 Z = (W-F) / 2
4. 字符间距 S = 2*Z + M
-->

<style lang="scss">
@import "../../scss/common/var";
@import "../input/var";
@import "../../scss/mixins/mixins";
@include b(pin-code){
  position: relative;
  @include e(group){
    width: 315px;
    height: 60px;
    position: absolute;
    z-index: 0;
    top: 0;
    left: 50%;
    margin: 0 -157px;
  }

  @include e(input){
    display: block;
    width: 340px;
    height: 60px;
    overflow: hidden;
    margin: 0 -157px;
    padding: 0;
    position: relative;
    left: 50%;
    border: none !important;
    background: none !important;
    box-shadow: none !important;
    z-index: 1;
    outline: 0;
    font-family: Menlo, Monaco, 'Courier New', monospace;
    &:focus {
      outline: none;
      & + .zm-pin-code__group .zm-pin-code--item.is-focus{
        box-shadow: $--base-focus-shadow;
      }
    }
    &::-ms-clear,&::-ms-reveal {
      display: none;
    }
  }
  @include m(item) {
    width: 44px;
    height: 60px;
    display: block;
    border-radius: 12px;
    border: 1px solid $--input-border-color;
    background: #fff;
    float: left;
    margin-right: 10px;
    &:last-child {
      margin-right: 0;
    }
  }
  &.zm-pin-code-small {
    .zm-pin-code__group{
      height: 40px;
    }
    .zm-pin-code__input{
      width: 320px;
      height: 40px;
      font-size: 20px;
      text-indent: 14px;
      letter-spacing: 36px;
    }
    .zm-pin-code--item{
      width: 40px;
      height: 40px;
      border-radius: 8px;
      margin-right: 8px;
    }
    .zm-pin-last-input-hover{
      width: 44px;
      height: 40px;
    }
  }
  &.zm-pin-code-middle {
    .zm-pin-code__group{
      height: 60px;
    }
    .zm-pin-code__input{
      height: 60px;
      font-size: 30px;
      text-indent: 13px;
      letter-spacing: 36px;
    }
    .zm-pin-code--item{
      height: 60px;
      border-radius: 12px;
    }
    .zm-pin-last-input-hover{
      width: 44px;
      height: 60px;
    }
  }
}

.zm-pin-last-input-hover {
  width: 32px;
  height: 60px;
  position: absolute;
  background-color: white;
  left: 50%;
  margin: 0 168px;
  z-index: 2;
}

.zm-theme--dark{
  .zm-pin-last-input-hover{
    background-color: #131619;
  }
  .zm-pin-code--item{
    background-color: transparent;
    border-color: #6E7680;
  }
  @include b(pin-code) {
    @include e(input) {
      &:focus {
        & + .zm-pin-code__group .zm-pin-code--item.is-focus {
          box-shadow: $--base-focus-shadow-dark;
        }
      }
    }
  }
}
@media (max-width: 430px) {
  .zm-pin-code {
    transform: scale(0.8);
  }
}
</style>
